import { memo, useCallback, useEffect, useMemo, useState } from "react";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { useParams } from "react-router-dom";
import { RenderWhen } from "../../../components";
import useCancelRequests from "../../../hooks/useCancelRequests";
import PreviewSample from "../components/PreviewSample";
import { useGetMergedRlcsReport } from "../components/rlcloudsandbox/hooks/useGetMergedRlcsReport";
import RlCloudSandboxAnalysisDropdown from "../components/rlcloudsandbox/screenshots/RlCloudSandboxAnalysisDropdown";
import RlCloudSandboxScreenshots from "../components/rlcloudsandbox/screenshots/RlCloudSandboxScreenshots";
import ScreenshotsErrorMessage from "../components/rlcloudsandbox/screenshots/ScreenshotsErrorMessage";
import Reanalyze from "../components/reanalyze/reanalyze-popup/Reanalyze";
import Visualization from "../components/visualization/Visualization";
import VisualizationLoadingHandler from "../components/visualization/VisualizationLoadingHandler";
import { useEmailPreview } from "../hooks";
import useVisualization from "../hooks/useVisualization";
import { INITIAL_ANALYSIS_SELECTIONS } from "../components/reanalyze/reanalyze-popup/consts";

interface PreviewAndVisualizationProps {
  threatStatus?: number;
  fileType: string;
  fileSubtype: string;
  typeDisplay: string | undefined;
  searchAddress?: string;
  sha1: string;
  sourceID: number | undefined;
  relationshipId: number | undefined;
  showOpenYaraRulesetButton?: boolean;
  initialRulesetName?: string;
  initialRuleName?: string;
  yaraTestRunResultId?: number;
  sourceSampleSha1?: string;
}

const PreviewAndVisualization = (props: PreviewAndVisualizationProps) => {
  const {
    sha1,
    fileType,
    fileSubtype,
    typeDisplay,
    sourceSampleSha1,
    relationshipId
  } = props;
  const { searchAddress } = useParams();

  const { query, source } = useVisualization(sha1);
  const isEmail = fileType.toLowerCase().startsWith("email");
  const { emailTextHash, emailHTMLHash } = useEmailPreview(
    sha1,
    typeDisplay ?? "",
    sourceSampleSha1,
    relationshipId ?? 0,
    isEmail
  );
  const {
    isPending: isLoading,
    data: { data: { structure = undefined } = {} } = {}
  } = query || {};

  useCancelRequests([source]);

  const sampleHasVisualization = useMemo(
    () => ["pe", "pe+"].includes(fileType?.toLowerCase()),
    [fileType]
  );

  const { mergedReportData, isSuccess: isMergedSuccess } =
    useGetMergedRlcsReport(sha1, false);

  const { history_analysis: historyAnalysis } = mergedReportData || {};

  const showTabForTextView =
    fileType === "Text" ||
    (fileType === "Media Container" && fileSubtype === "None") ||
    (fileType === "Binary" && fileSubtype === "None");
  const showTabForImageView =
    fileType.includes("Image") &&
    ["jpg:", "jpeg:", "png:", "gif:"].some(e =>
      typeDisplay?.toLowerCase().startsWith(e)
    );
  const hasVisualizationPlot = Object.keys(structure || {}).length > 0;

  const [activeAnalysisId, setActiveAnalysisId] = useState(
    historyAnalysis?.[0]?.analysis_id
  );
  const [isReanalyzeOpen, setIsReanalyzeOpen] = useState(false);
  const handleOpenReanalyze = useCallback(() => setIsReanalyzeOpen(true), []);
  const handleCloseReanalyze = useCallback(() => setIsReanalyzeOpen(false), []);

  useEffect(() => {
    if (!activeAnalysisId) {
      setActiveAnalysisId(historyAnalysis?.[0]?.analysis_id);
    }
  }, [activeAnalysisId, historyAnalysis]);

  return (
    <div className="rlbs preview-visualization">
      <h1 className="sample-details__page-title">
        Preview Sample / Visualization
      </h1>
      <Tabs
        defaultActiveKey={1}
        id="preview-visualization-tabs"
        transition={false}
      >
        <Tab key={1} eventKey={1} title="Hex">
          <PreviewSample
            {...props}
            previewType="hex"
            searchAddress={searchAddress}
          />
        </Tab>
        {showTabForTextView && (
          <Tab key={2} eventKey={2} title="Text">
            <PreviewSample {...props} previewType="text" />
          </Tab>
        )}
        {showTabForImageView && (
          <Tab key={3} eventKey={3} title="View Image">
            <PreviewSample {...props} previewType="image" />
          </Tab>
        )}
        {sampleHasVisualization && (
          <Tab key={4} eventKey={4} title="Structure">
            <VisualizationLoadingHandler
              isLoading={isLoading}
              Component={
                <Visualization
                  {...props}
                  hasPlot={hasVisualizationPlot}
                  structure={structure}
                  previewType="structure"
                />
              }
            />
          </Tab>
        )}
        {sampleHasVisualization && (
          <Tab key={5} eventKey={5} title="Entropy">
            <VisualizationLoadingHandler
              isLoading={isLoading}
              Component={
                <Visualization
                  {...props}
                  hasPlot={hasVisualizationPlot}
                  structure={structure}
                  previewType="entropy"
                />
              }
            />
          </Tab>
        )}
        {emailHTMLHash && (
          <Tab key={6} eventKey={6} title="HTML">
            <PreviewSample
              sha1={emailHTMLHash}
              fileType="Text"
              fileSubtype="HTML"
              typeDisplay="HTML:Generic"
              previewType="text"
            />
          </Tab>
        )}
        {emailTextHash && (
          <Tab key={7} eventKey={7} title="Text">
            <PreviewSample
              sha1={emailTextHash}
              fileType="Text"
              fileSubtype="None"
              typeDisplay="Text/None"
              previewType="text"
            />
          </Tab>
        )}
        <Tab key={8} eventKey={8} title="RLCS Screenshots">
          <div style={{ marginBottom: "10px" }}></div>
          {isMergedSuccess ? (
            <>
              <RlCloudSandboxAnalysisDropdown
                historyAnalysis={historyAnalysis}
                activeAnalysisId={activeAnalysisId}
                setActiveAnalysisId={setActiveAnalysisId}
              />
              <RlCloudSandboxScreenshots
                hash={sha1}
                isNetwork={false}
                activeAnalysisId={activeAnalysisId}
              />
            </>
          ) : (
            <>
              <ScreenshotsErrorMessage onReanalyze={handleOpenReanalyze} />
              <RenderWhen condition={isReanalyzeOpen}>
                <Reanalyze
                  samples={[{ hash_value: sha1 }]}
                  onClose={handleCloseReanalyze}
                  initial={{
                    ...INITIAL_ANALYSIS_SELECTIONS,
                    rl_dynamic_analysis: true
                  }}
                />
              </RenderWhen>
            </>
          )}
        </Tab>
      </Tabs>
    </div>
  );
};

export default memo(PreviewAndVisualization);
